import React, { useMemo, useState, useEffect } from "react";
import { Spacer } from "components/base/Spacer";
import { Drawer, Schema, FlexboxGrid, Icon, IconButton } from "rsuite";
import styled from "styled-components";

import { LS_DIALER_TEST_CALLS_PER_PAGE } from "const/localStorageKeys";
import {
    LOCAL_STORAGE_TEST_CALLS_FILTERS_DIALER,
    MAX_PER_PAGE_DIALER,
    MIN_PER_PAGE_DIALER
} from "const";
import m from "definedMessages";
import { FormattedMessage } from "react-intl";
import { withTranslation } from "hoc/WithTranslation";
import { useWindowWidth } from "hooks";
import Page from "components/base/Page";
import PageHeader from "components/base/PageHeader";
import { FlexGrid, FlexGridItem } from "components/base/FlexGrid";
import { removeTZFromDate, servicePick } from "utils/helpers";
import { resolvePerPage } from "utils";

import TestCallsTable from "./TestCallsTable";
import TestCallsFilters from "./TestCallsFilters";
import { DEFAULT_PER_PAGE_DIALER } from "const";

const MAX_CHARACTERS = 40;
const defaultFilter = {
    start_date: removeTZFromDate(new Date(new Date().setHours(0, 0, 0, 0))),
    end_date: removeTZFromDate(new Date(new Date().setHours(23, 59, 59, 999)))
};

const { StringType, ArrayType } = Schema.Types;

const TestCalls = ({
    data = [],
    more = false,
    count = 0,
    page = 1,
    perPage,
    service,
    loading,

    formatMessage,
    getDialerTestCalls
}) => {
    const savedTableFilter = JSON.parse(localStorage.getItem(LOCAL_STORAGE_TEST_CALLS_FILTERS_DIALER));

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 768;

    const [filter, setFilter] = useState(defaultFilter);

    const resolvedPerPage = useMemo(() => resolvePerPage(
        LS_DIALER_TEST_CALLS_PER_PAGE,
        perPage,
        DEFAULT_PER_PAGE_DIALER,
        MIN_PER_PAGE_DIALER,
        MAX_PER_PAGE_DIALER
    ), [perPage]);

    const [currentPerPage, setCurrentPerPage] = useState(resolvedPerPage);
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);
    const [showFilters, setShowFilters] = useState(false);

    const filtersModel = useMemo(() => {
        return Schema.Model({
            start_end_date: ArrayType()
                .addRule((value) => {
                    if (value && value[0] && value[1]) {
                        const startDate = new Date(value[0]);
                        const endDate = new Date(value[1]);
                        const startDay = startDate.getDate();
                        const endDay = endDate.getDate();

                        if ((endDay - startDay) > 1) {
                            return false;
                        }
                    }
                    return true;
                }, formatMessage(m.filterAvailableForTwoDays)),
            ...servicePick(service,
                {
                    a_number: StringType()
                        .pattern(/^\d+$/, formatMessage(m.correctNumber))
                        .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS }))
                },
                {
                    senderid: StringType()
                        .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS }))
                }
            ),
            [servicePick(service, "b_number", "phone")]: StringType()
                .pattern(/^\d+$/, formatMessage(m.correctNumber))
                .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS })),
        });
    }, [formatMessage, service]);

    useEffect(() => {
        setCurrentPerPage(resolvedPerPage);
    }, [resolvedPerPage]);

    useEffect(() => {
        const savedFilter = savedTableFilter && Object.keys(savedTableFilter).length
            ? savedTableFilter
            : filter;

        if (savedFilter !== filter) {
            const preparedFilter = {
                ...savedFilter,
                start_date: new Date(savedFilter.start_date),
                end_date: new Date(savedFilter.end_date)
            }

            setFilter(preparedFilter);
        }

        onSetActiveFiltersCount(savedFilter);
    }, []);

    const getItems = (nextPage = 1, nextPerPage = currentPerPage, nextFilter = filter) => {
        const checker = filtersModel.check(nextFilter);
        const formHasError = Object.keys(checker).some(key => checker[key].hasError);

        if (formHasError) {
            return;
        }

        setCurrentPerPage(nextPerPage);

        getDialerTestCalls(service, nextPerPage, nextFilter, nextPage);
    };

    useEffect(() => {
        if (service === null) return;

        const savedFilter = savedTableFilter && Object.keys(savedTableFilter).length ? savedTableFilter : null;

        const checker = savedFilter ? filtersModel.check(savedFilter) : null;
        const formHasError = checker ? Object.keys(checker).some(k => checker[k].hasError) : null;

        const pickedFilter = formHasError
            ? savedFilter
            : filter || defaultFilter;

        getItems(1, resolvedPerPage, pickedFilter || defaultFilter);
    }, [service, resolvedPerPage]);

    const onSetSavedFilters = (value) => {
        localStorage.setItem(LOCAL_STORAGE_TEST_CALLS_FILTERS_DIALER, JSON.stringify(value));
    };

    const onSetActiveFiltersCount = (value = null) => {
        if (!value)
            return;

        const filters = Object.keys(value);
        const dateFilters = ["start_end_date", "start_date", "end_date"];
        const activeFilters = filters.filter(item => {
            if (dateFilters.includes(item))
                return false;

            if (value[item] === defaultFilter[item] || (!value[item] && value[item] !== 0))
                return false;

            return true;
        });

        setActiveFiltersCount(activeFilters.length + 1);
    };

    const onChangeFilters = (value) => {
        onSetActiveFiltersCount(value);
        onSetSavedFilters(value);
        setFilter(value);

        getItems(1, currentPerPage, value);
    };


    return (
        <Page>
            <FlexboxGrid align="middle" justify="space-between">
                <FlexboxGrid.Item>
                    <PageHeader
                        title={<FormattedMessage id="menu.testResults" defaultMessage="Test Results" />}
                    />
                </FlexboxGrid.Item>

                {isMobile &&
                    <FlexboxGrid.Item>
                        <Toggler
                            icon={<Icon icon="filter" />}
                            onClick={() => setShowFilters(true)}
                        >
                            {activeFiltersCount}
                        </Toggler>
                        <Drawer
                            size="xs"
                            placement="bottom"
                            style={{ height: "240px" }}
                            show={showFilters}
                            onHide={() => setShowFilters(false)}
                        >
                            <Drawer.Header>
                            </Drawer.Header>
                            <Drawer.Body>
                                <TestCallsFilters
                                    onChangeFilters={onChangeFilters}
                                    loading={loading}
                                    service={service}
                                    filtersModel={filtersModel}
                                    {...{
                                        savedTableFilter
                                    }}
                                    formatMessage={formatMessage}
                                />
                            </Drawer.Body>
                        </Drawer>
                    </FlexboxGrid.Item>

                }

            </FlexboxGrid>

            {!isMobile &&
                <FlexGrid>
                    <FlexGridItem>
                        <TestCallsFilters
                            onChangeFilters={onChangeFilters}
                            loading={loading}
                            service={service}
                            filtersModel={filtersModel}
                            {...{
                                savedTableFilter
                            }}
                            formatMessage={formatMessage}
                        />
                        <Spacer />
                    </FlexGridItem>
                </FlexGrid>
            }

            <TestCallsTable
                data={data}
                loading={loading}
                {...{
                    more,
                    page,
                    count,
                    service,
                    perPage: currentPerPage,
                    getItems,

                    formatMessage
                }}
            />
        </Page>
    );
};

export default withTranslation(TestCalls);


const Toggler = styled(IconButton)`
    && {
        color: #363434;
    }
`;